import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"
import * as members from "../members"

// QC sign-off capabilities attached to a member's profile (F-A3; SPEC-021 B3). They are
// independent of the planning role (member/planner/view_only): a person can be "Member + QA"
// or "Planner + Reviewer".
//
// SPEC-021 cutover: capabilities are READ off the member dump (`member.qcCapabilities` — the
// generic members endpoints serialize them read-only) and CHANGED only via the e-signed
// `POST /lims/qc-capabilities` (hyphenated) with `{input:{memberId, capabilities, password,
// reason}}`. There is NO GET and no separate redux slice state any more — this module is
// helpers + the signed write thunk.
//
// Authorisation (SPEC-007 D2 rev. 2026-06-11, enforced server-side and mirrored here for the
// UI): a member with the PLANNER planning role administers QC capabilities for their team; a
// holder of the "qa-manager" capability may also (delegated administration). Anyone else
// cannot. (The earlier qa-manager-only rule with an ADMIN bootstrap was dropped: ADMIN-role
// members have no team-setup UI, so the first qa-manager could never be granted in-app.)
// Ordered to mirror the QC pipeline (analyst → reviewer → supervisor → qa → qa-manager) so the
// MemberConfig checkboxes read in flow order. `supervisor` (SPEC-032 / F0) is the chemist role
// that owns lab analysis-review and stamdata change-control; the result flow does not consume it
// yet (that is F3). The backend QcCapabilitiesUpdate validator accepts the same set.
export const QC_CAPABILITIES = ["analyst", "reviewer", "supervisor", "qa", "qa-manager"]

// The capability that allows DELEGATED capability administration (a planner may grant it to a
// QC lead who is not themselves a planner).
export const QA_MANAGER = "qa-manager"

// Set a member's QC capabilities behind an electronic signature. `signature` is the
// ESignatureModal payload; its password (+ reason) ride at the TOP LEVEL of the `input`
// envelope per the backend schema (QcCapabilitiesUpdate). On success the members store is
// refreshed so `member.qcCapabilities` reflects the change everywhere.
export const setForMember = asyncAction(
  "limsCapabilities/set",
  (dispatch, memberId, capabilities, signature = null) => {
    return limsRequest("POST", "/lims/qc-capabilities", {
      input: {
        memberId,
        capabilities: capabilities || [],
        password: (signature && signature.password) || undefined,
        reason: (signature && signature.reason) || null,
      },
    }).then((res) => {
      // Re-read the members so the updated qcCapabilities land in the store (the capability
      // checks below read straight off the member records).
      dispatch(members.fetch())
      return res.item
    })
  }
)

// The QC capabilities currently held by a member (read off the member dump).
export function capabilitiesForMember(state, memberId) {
  const member = memberId && state.members && state.members[memberId]
  return (member && member.qcCapabilities) || []
}

// Whether the given member currently has the given QC capability.
export function hasQcCapability(state, memberId, capability) {
  return capabilitiesForMember(state, memberId).includes(capability)
}

// May the CURRENT user administer QC capabilities? Mirrors the server (SPEC-007 D2 rev.):
// a PLANNER (team lead), or a holder of the "qa-manager" capability (delegated). Returns a
// boolean.
export function selectCanAdministerCapabilities(state) {
  if (state.auth.role === "planner") {
    return true
  }
  return hasQcCapability(state, state.auth.memberId, QA_MANAGER)
}
